import { PlaybackHistogram } from '../analytics/playback-histogram';
import { EventEmitterBase, Guid, Timer, TimeSpanUtils } from '../utils';
import { ContentType, MediaControllerState, PlaybackSessionEvents } from './enums';
import { IContent, IPlaybackStats, IPlayerSettings, TimeSpan } from './models';
import { PlaybackSessionState } from './playback-session-state';

export class PlaybackSession extends EventEmitterBase {

    private _playbackStalls: number = 0;

    private _firstFrameTs: number = 0;
    private _firstPlayTs: number = 0;
    private _idleTimeout: number = 0;
    private _bufferTimer: Timer = new Timer();
    private _lastActivity: number;

    private _isSeeking: boolean;

    state: PlaybackSessionState;

    constructor(playerId: string) {
        super();
        this.state = new PlaybackSessionState();
        this.state.id = Guid.v1();
        this.state.playerId = playerId;
        this.on('event', this.startIdleTimeout);
        this.on(PlaybackSessionEvents.SessionEnded, this.stopUpdateTimer);
    }

    getId(): string {
        return this.state.id;
    }

    updateStats(stats: IPlaybackStats): void {
        this.state.playbackStats = stats;
    }

    load(content: IContent, settings: IPlayerSettings): void {
        this.state.content = content;
        this.state.playerSettings = settings;
        this.state.playbackHistogram = new PlaybackHistogram(0, content.duration.value * 1000, 100);
        this.emit(PlaybackSessionEvents.ContentLoaded, this.state);
    }

    reset(): void {
        this._playbackStalls = 0;

        const content: IContent = this.state.content;
        const settings: IPlayerSettings = this.state.playerSettings;
        const sessId: string = Guid.v1();
        const playerId: string = this.state.playerId;

        this.state = new PlaybackSessionState();
        this.state.id = sessId;
        this.state.playerId = playerId;
        this.load(content, settings);
    }

    start(position: number): void {
        if (!this._bufferTimer.stopped) {
            this.bufferEnd();
        }

        this._isSeeking = false;
        this.resetPlaybackSegment(position);

        if (!this.state.started) {
            this.state.started = true;
            this.emit(PlaybackSessionEvents.SessionStarted, this.state);
        } else {
            this.emit(PlaybackSessionEvents.SessionResumed, this.state);
        }
    }

    resetPlaybackSegment(position?: number): void {
        this.state.currentSegment = {
            id: Guid.v1(),
            name: 'content_playback',
            type: 'content_playback',
            start: typeof (position) === 'number' ? position * 1000 : undefined,
            end: undefined,
            duration: 0,
            buckets: [],
        };
    }

    seekStart(): void {
        this._isSeeking = true;
    }

    seekEnd(position: number): void {
        this.state.currentPosition = position;
        this.resetPlaybackSegment(position);
        this.emit(PlaybackSessionEvents.Seeked, this.state);
    }

    updatePosition(position: number, state: MediaControllerState): void {
        // Seekable ranges changed, time is still the same
        // We ignore the update
        if (position === this.state.currentPosition) {
            return;
        }

        this._isSeeking = (position - this.state.currentPosition) > 3;

        if (this._isSeeking) {
            this.state.currentPosition = position;
            return;
        }
        if (state !== MediaControllerState.Playing) {
            return;
        }

        const diff: number = position - this.state.currentPosition;

        this.state.currentSegment.buckets = this.state.playbackHistogram.map(this.state.currentSegment);
        this.state.currentSegment.end = position * 1000;
        this.state.currentSegment.duration = this.state.currentSegment.end - this.state.currentSegment.start;
        this.state.totalPlaybackTime += diff;
        this.state.currentPosition = position;
    }

    pause(position: number, state: MediaControllerState): void {
        if (state === MediaControllerState.Buffering) {
            this.bufferStart();
            this._playbackStalls++;
            return;
        }
        if (this.state.content.playbackType === ContentType.Live) {
            position = Date.now() / 1000;
        }
        this.updatePlaybackInfo(position);
        this.emit(PlaybackSessionEvents.Paused, {...this.state});
        this.trackSegment();
    }

    updatePlaybackInfo(position: number): void {
        if (this._isSeeking) {
            return;
        }

        if (this.state.currentSegment) {
            this.state.currentSegment.end = position * 1000;
            this.state.currentSegment.duration = this.state.currentSegment.end - this.state.currentSegment.start;
        }
    }

    trackSegment(): void {
        if (this.state.currentSegment && this.state.currentSegment.buckets.length > 0) {
            this.state.currentSegment.buckets = this.state.playbackHistogram.map(this.state.currentSegment);
            const segment: TimeSpan = {
                ...this.state.currentSegment,
                buckets: [...this.state.currentSegment.buckets],
            };
            this.state.playedSegments.push(segment);
            this.state.playbackHistogram.add(segment);
            this.state.currentSegment = null;
        }
    }

    end(completed: boolean = false): void {
        this.state.completed = completed;
        this.stopUpdateTimer();
        this.emit(PlaybackSessionEvents.SessionEnded, this.state);
    }

    stopUpdateTimer(): void {
        if (this._idleTimeout && typeof this._idleTimeout === 'number') {
            self.clearInterval(this._idleTimeout);
            this._idleTimeout = null;
        }
    }

    startIdleTimeout(): void {
        this.stopUpdateTimer();
        this._idleTimeout = self.setTimeout(() => {
        }, TimeSpanUtils.FromMinutes(1));
    }

    destroy(): void {
        this.end(false);
        this.emit(PlaybackSessionEvents.Destroyed, this.state);
        super.removeAllListeners();
        this.state = null;
    }

    bufferStart(): void {
        this._bufferTimer = new Timer();
        this._bufferTimer.start();
        this.state.bufferEvents++;
    }

    bufferEnd(): void {
        this._bufferTimer.stop();
        this.state.totalBufferingTime += this._bufferTimer.elapsedMilliseconds();
    }

    firstFrame(): void {
        this._firstFrameTs = Date.now();
        this.state.timeToFirstFrame = this._firstFrameTs - this._firstPlayTs;
    }

    firstPlay(): void {
        this._firstPlayTs = Date.now();
    }
}
